import { THEMES, themeTokens, type SearchProvider, type UserSettings } from '@flame/shared';
import { mdiDelete } from '../../components/icons';
import { useState } from 'preact/hooks';
import { Api } from '../../api';
import { Svg } from '../../components/Icon';
import { Field } from '../../components/ui';
import { allProviders, LOCAL_PREFIX } from '../../search/search';
import { attempt, setSettings, settings } from '../../state';

type Patch = Partial<{ [K in keyof UserSettings]: Partial<UserSettings[K]> }>;

/** Apply locally right away (live preview), then persist. */
async function save(patch: Patch, success?: string) {
  const before = settings.value;
  const optimistic = { ...before } as Record<string, unknown>;
  for (const [group, values] of Object.entries(patch)) {
    optimistic[group] = { ...before[group as keyof UserSettings], ...values };
  }
  setSettings(optimistic as UserSettings);
  const res = await attempt(() => Api.updateSettings(patch), success);
  setSettings(res ? res.settings : before);
}

function Swatch({ name, mode }: { name: string; mode: 'light' | 'dark' }) {
  const t = themeTokens(name, mode, settings.value.appearance.accent);
  return (
    <span class="swatch" style={{ background: t.bg, color: t.fg, borderColor: t.border }}>
      <span class="swatch-text">Aa</span>
      <span class="swatch-accent" style={{ background: t.accent }} />
    </span>
  );
}

export function Appearance() {
  const a = settings.value.appearance;
  const [css, setCss] = useState(a.customCss);
  return (
    <>
      <h2>Appearance</h2>
      <fieldset class="segmented">
        <legend class="field-label">Colour scheme</legend>
        {(['system', 'light', 'dark'] as const).map((mode) => (
          <label key={mode}>
            <input
              type="radio"
              name="mode"
              checked={a.mode === mode}
              onChange={() => save({ appearance: { mode } })}
            />
            {mode === 'system' ? 'Match device' : mode === 'light' ? 'Light' : 'Dark'}
          </label>
        ))}
      </fieldset>

      <h3>Theme</h3>
      <p class="muted">Every theme has a light and a dark version.</p>
      <div class="theme-grid" role="radiogroup" aria-label="Theme">
        {Object.keys(THEMES).map((name) => (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={a.theme === name}
            class={`theme-card${a.theme === name ? ' active' : ''}`}
            onClick={() => save({ appearance: { theme: name } })}
          >
            <span class="theme-swatches">
              <Swatch name={name} mode="light" />
              <Swatch name={name} mode="dark" />
            </span>
            <span class="theme-name">{name}</span>
          </button>
        ))}
      </div>

      <div class="field-row">
        <Field label="Accent colour" hint="Overrides the theme's accent. Adjusted if needed so it stays readable.">
          <input
            type="color"
            value={a.accent ?? themeTokens(a.theme, 'dark').accent}
            onChange={(e) => save({ appearance: { accent: e.currentTarget.value } })}
          />
        </Field>
        {a.accent && (
          <button type="button" class="button align-end" onClick={() => save({ appearance: { accent: null } })}>
            Use theme accent
          </button>
        )}
      </div>

      <h3>Custom CSS</h3>
      <p class="muted">
        Applied only to your own pages. Theme colours are available as <code>var(--bg)</code>,{' '}
        <code>var(--fg)</code>, <code>var(--accent)</code>, <code>var(--surface)</code> and{' '}
        <code>var(--muted)</code>.
      </p>
      <textarea
        class="code"
        rows={8}
        value={css}
        spellcheck={false}
        onInput={(e) => setCss(e.currentTarget.value)}
        aria-label="Custom CSS"
      />
      <div class="form-actions">
        <button
          type="button"
          class="button button-primary"
          disabled={css === a.customCss}
          onClick={() => save({ appearance: { customCss: css } }, 'Custom CSS saved.')}
        >
          Save CSS
        </button>
      </div>
    </>
  );
}

export function PageSettings() {
  const { layout, links } = settings.value;
  return (
    <>
      <h2>Page</h2>
      <Field label={`Columns: ${layout.columns}`} hint="Fewer columns are used on narrow screens.">
        <input
          type="range"
          min={1}
          max={8}
          value={layout.columns}
          onChange={(e) => save({ layout: { columns: Number(e.currentTarget.value) } })}
        />
      </Field>
      <fieldset class="segmented">
        <legend class="field-label">Density</legend>
        {(['comfortable', 'compact'] as const).map((density) => (
          <label key={density}>
            <input
              type="radio"
              name="density"
              checked={layout.density === density}
              onChange={() => save({ layout: { density } })}
            />
            {density === 'comfortable' ? 'Comfortable' : 'Compact'}
          </label>
        ))}
      </fieldset>
      <label class="check">
        <input
          type="checkbox"
          checked={layout.showSearch}
          onChange={(e) => save({ layout: { showSearch: e.currentTarget.checked } })}
        />
        Show the search bar
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={layout.showClock}
          onChange={(e) => save({ layout: { showClock: e.currentTarget.checked } })}
        />
        Show the date and time
      </label>
      <fieldset class="segmented">
        <legend class="field-label">Open links in</legend>
        {(['new_tab', 'same_tab'] as const).map((openIn) => (
          <label key={openIn}>
            <input
              type="radio"
              name="openIn"
              checked={links.openIn === openIn}
              onChange={() => save({ links: { openIn } })}
            />
            {openIn === 'new_tab' ? 'A new tab' : 'The same tab'}
          </label>
        ))}
      </fieldset>
      <p class="muted">Individual links can override this in their edit dialog.</p>
    </>
  );
}

export function SearchSettings() {
  const s = settings.value;
  const providers = allProviders(s);
  const [draft, setDraft] = useState<SearchProvider>({ name: '', prefix: '', template: '' });

  const setProviders = (next: SearchProvider[], success?: string) =>
    save({ search: { providers: next } }, success);

  return (
    <>
      <h2>Search</h2>
      <p class="muted">
        Type <code>/prefix query</code> to search a specific site, e.g. <code>/yt lofi</code>.
        Otherwise the search bar looks through your links.
      </p>
      <div class="field-row">
        <Field label="Default search">
          <select
            value={s.search.defaultPrefix}
            onChange={(e) => save({ search: { defaultPrefix: e.currentTarget.value } })}
          >
            {providers.map((p) => (
              <option key={p.prefix} value={p.prefix}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="When no link matches">
          <select
            value={s.search.secondaryPrefix}
            onChange={(e) => save({ search: { secondaryPrefix: e.currentTarget.value } })}
          >
            {providers
              .filter((p) => p.prefix !== LOCAL_PREFIX)
              .map((p) => (
                <option key={p.prefix} value={p.prefix}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
      </div>

      <h3>Search providers</h3>
      <table class="table">
        <thead>
          <tr>
            <th>Prefix</th>
            <th>Name</th>
            <th>URL</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => {
            const custom = s.search.providers.some((c) => c.prefix === p.prefix);
            return (
              <tr key={p.prefix}>
                <td>
                  <code>/{p.prefix}</code>
                </td>
                <td>{p.name}</td>
                <td class="truncate">{p.prefix === LOCAL_PREFIX ? 'Your links' : p.template}</td>
                <td>
                  {custom && (
                    <button
                      type="button"
                      class="icon-button"
                      aria-label={`Remove ${p.name}`}
                      onClick={() =>
                        setProviders(s.search.providers.filter((c) => c.prefix !== p.prefix))
                      }
                    >
                      <Svg path={mdiDelete} size={18} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <form
        class="form provider-form"
        onSubmit={(e) => {
          e.preventDefault();
          const next = [
            ...s.search.providers.filter((c) => c.prefix !== draft.prefix.toLowerCase()),
            { ...draft, prefix: draft.prefix.toLowerCase() },
          ];
          void setProviders(next, `Added /${draft.prefix}.`).then(() =>
            setDraft({ name: '', prefix: '', template: '' })
          );
        }}
      >
        <h3>Add a provider</h3>
        <div class="field-row">
          <Field label="Prefix">
            <input
              required
              pattern="[a-zA-Z0-9]{1,8}"
              value={draft.prefix}
              onInput={(e) => setDraft({ ...draft, prefix: e.currentTarget.value })}
              placeholder="k"
            />
          </Field>
          <Field label="Name">
            <input
              required
              value={draft.name}
              onInput={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
              placeholder="Kagi"
            />
          </Field>
        </div>
        <Field label="Search URL" hint={<>Use <code>{'{q}'}</code> where the query goes, or end the URL with it.</>}>
          <input
            required
            type="url"
            value={draft.template}
            onInput={(e) => setDraft({ ...draft, template: e.currentTarget.value })}
            placeholder="https://kagi.com/search?q={q}"
          />
        </Field>
        <div class="form-actions">
          <button type="submit" class="button button-primary">
            Add provider
          </button>
        </div>
      </form>
    </>
  );
}
